import { apiClient } from "./apiClient";

export type CampaignActionType = "none" | "url" | "subscription" | "course_booking" | "payment" | "coupon";

export type CampaignButtonStyle = {
  backgroundColor?: string;
  textColor?: string;
  borderRadius?: string;
  size?: "sm" | "md" | "lg";
  align?: "left" | "center" | "right";
} | null;

type CampaignActionUnion =
  | { action_type: "none"; action_payload: null }
  | { action_type: "url"; action_payload: null }
  | {
      action_type: "subscription";
      action_payload: { subscription_plan_id: number; course_id: number; course_slug: string; coupon_code?: string | null };
    }
  | { action_type: "course_booking"; action_payload: { course_id: number; course_slug: string } }
  | {
      action_type: "payment";
      action_payload: { payment_context: "invoice" | "wallet_topup" | "subscription_renewal"; payment_reference?: string | number | null };
    }
  | { action_type: "coupon"; action_payload: { coupon_id: number; coupon_code?: string | null } };

/**
 * The whitelist of valid `zone` values — must mirror config('campaigns.zones')
 * on the backend. Two independent pairs, each mounted once in its own shared
 * layout: `dashboard_top`/`dashboard_bottom` in the Student Dashboard layout
 * (app/[locale]/(dashboard)/student/layout.tsx, every /student page), and
 * `site_top`/`site_bottom` in the public/site layout
 * (app/[locale]/(web)/layout.tsx, every public page). Neither pair reaches
 * the other route group — (auth), /impersonate, error/not-found inherit none.
 */
export const CAMPAIGN_ZONES = ["dashboard_top", "dashboard_bottom", "site_top", "site_bottom"] as const;
export type CampaignZoneName = (typeof CAMPAIGN_ZONES)[number];

/** Normalized percentages (0-100) relative to the Campaign's own container — never viewport/pixel coordinates. A freely draggable point, never restricted to predefined positions. */
export type CampaignCountdownXY = { x: number; y: number };

/**
 * Campaigns created before per-locale Countdown positioning existed may
 * still hold the legacy flat {x, y} shape — applied identically regardless
 * of locale, never migrated or reinterpreted. The current shape nests an
 * independent {x, y} per locale; Arabic is optional and falls back to
 * English when unset (English and Arabic are never mirrored or calculated
 * from one another — each is independently configured).
 */
export type CampaignCountdownPosition = CampaignCountdownXY | { en?: CampaignCountdownXY; ar?: CampaignCountdownXY };

/** Mirrors the CSS `object-fit` values — applied verbatim to the rendered campaign image. Only meaningful for type "banner"/"floating". */
export type CampaignImageFit = "contain" | "cover" | "fill" | "none" | "scale-down";

export type CampaignCountdownExpiryBehavior = "stop_at_zero" | "hide";

interface BaseCampaignFields {
  id: number;
  /** Stable, unique per-campaign DOM/CSS id (e.g. "campaign-a1b2c3d4") — used to scope Custom CSS/JS to this campaign only. */
  css_id: string;
  title: string;
  type: "modal" | "top_bar" | "bottom_bar" | "floating" | "banner";
  /** The named placement a `banner` renders inline into. Always null for every other type. */
  zone: CampaignZoneName | null;
  content_type: "text" | "image" | "html" | "video";
  content: string | null;
  image: string | null;
  /** Desktop object-fit for `image`. Independent of image_fit_mobile — see useIsMobile for which one applies. */
  image_fit_desktop: CampaignImageFit;
  /** Mobile object-fit for `image`. Independent of image_fit_desktop — see useIsMobile for which one applies. */
  image_fit_mobile: CampaignImageFit;
  button_text: string | null;
  button_url: string | null;
  button_style: CampaignButtonStyle;
  position: string;
  mobile_position: string | null;
  animation: "fade" | "slideDown" | "slideUp" | "zoom" | "bounce";
  /** Background of the whole Campaign container. Independent of countdown_background_color. Null = use the stylesheet's own default. */
  background_color: string | null;
  width: string | null;
  height: string | null;
  mobile_width: string | null;
  mobile_height: string | null;
  custom_css: string | null;
  custom_js: string | null;
  display_seconds: number | null;
  close_type: "manual" | "auto" | "both";
  blocking: boolean;
  sticky: boolean;
  priority: number;
  frequency: "always" | "once_session" | "once_day" | "once_user";
  /**
   * Entirely independent of the Campaign's own lifecycle (start_at/end_at,
   * not exposed here) — a Countdown reaching zero never deactivates the
   * Campaign.
   */
  show_countdown: boolean;
  countdown_end_at: string | null;
  countdown_position: CampaignCountdownPosition | null;
  /** Optional override for small viewports — falls back to countdown_position when null. */
  countdown_position_mobile: CampaignCountdownPosition | null;
  countdown_expiry_behavior: CampaignCountdownExpiryBehavior;
  /** Background of the Countdown element only. Independent of background_color. Null = use the stylesheet's own default. */
  countdown_background_color: string | null;
  /** Color of the Countdown's numbers, units, and "Left"/"متبقي" label. Independent of countdown_background_color and background_color. Null = use the stylesheet's own default. */
  countdown_text_color: string | null;
  /** Font size (px) of the Countdown's text/numbers only. Never affects any other Campaign text. */
  countdown_font_size: number;
  /** Whether the "Left"/"متبقي" label renders next to the Countdown. */
  show_countdown_label: boolean;
  /** Whether the Days unit stays visible once it reaches zero. Never affects the countdown calculation itself, and Days is always shown while greater than zero regardless of this setting. */
  show_days_when_zero: boolean;
}

export type Campaign = BaseCampaignFields & CampaignActionUnion;

export type CampaignEvent =
  | "view"
  | "click"
  | "close"
  | "checkout_started"
  | "payment_started"
  | "payment_success"
  | "payment_failed";

export const getActiveCampaigns = async (
  page: string,
  regionSlug?: string,
  locale?: string,
  device?: "desktop" | "mobile"
): Promise<Campaign[]> => {
  const params: Record<string, string> = { page };
  if (regionSlug) {
    params.region_slug = regionSlug;
  }
  if (locale) {
    params.locale = locale;
  }
  if (device) {
    params.device = device;
  }
  const response = await apiClient.get<{ data: Campaign[] }>("/api/campaigns/active", { params });
  return response.data.data;
};

export const trackCampaignEvent = async (
  campaignId: number,
  event: CampaignEvent,
  sessionId: string
): Promise<{ success: boolean }> => {
  const response = await apiClient.post<{ success: boolean }>(`/api/campaigns/${campaignId}/event`, {
    event,
    session_id: sessionId,
  });
  return response.data;
};
